import { useState, type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useI18n } from '../i18n/index.js';
import { PlusIcon } from './icons.js';
import styles from './PickGrid.module.css';

export interface PickGridItem {
  categoryId: string;
  categoryName: string;
  itemName: string;
  imageUrl?: string | undefined;
}

/**
 * Pick 격자 — 한 줄에 3칸.
 *
 * **카테고리와 이름을 둘 다 보여준다.** 둘 중 하나만으로는 목록이 읽히지 않는다:
 * 이름만 있으면 `HHKB`가 무엇인지 모르는 사람에게 아무 의미가 없고, 카테고리만
 * 있으면 이 사람이 *무엇을* 골랐는지가 빠진다. 서비스의 문장이 "키보드에서
 * 하나 고른다면 HHKB"라서 두 단어가 함께 있어야 문장이 된다.
 *
 * 칸 아래쪽 같은 자리에 두 줄로 겹쳐 둔다 — 사진이 있든 없든 글자가 같은 높이에
 * 오므로 격자를 훑을 때 눈이 한 줄로 움직인다.
 *
 * 4:5 세로형인 이유: 480px 기둥에서 3칸이면 한 칸이 148px인데, 정사각형이면
 * 사진과 두 줄 글자가 같이 들어가기에 빠듯하다.
 */
export function PickGrid({ children }: { children: ReactNode }) {
  return <ul className={styles.grid}>{children}</ul>;
}

interface SortableGridProps {
  /** 순서대로의 categoryId. 이 배열이 곧 화면 순서다 */
  order: string[];
  /** 새 순서. 저장은 호출자가 한다 */
  onReorder: (next: string[]) => void;
  children: ReactNode;
}

/**
 * 끌어서 순서를 바꿀 수 있는 격자.
 *
 * **길게 눌러야 시작된다.** 격자 칸은 누르면 수정 화면이 열리는데, 손가락이
 * 닿자마자 드래그가 시작되면 스크롤도 탭도 안 된다. 아이폰 홈 화면과 같은
 * 방식이라 따로 배울 것이 없다.
 *
 * 마우스는 즉시 시작하되 8px은 움직여야 한다 — 그래야 클릭과 구분된다.
 */
export function SortablePickGrid({ order, onReorder, children }: SortableGridProps) {
  const { t } = useI18n();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    /*
     * 손을 못 쓰는 사람도 순서를 바꿀 수 있어야 한다.
     * Space로 집고 화살표로 옮긴 뒤 Space로 놓는다.
     *
     * ⚠️ **Enter는 넘겨주지 않는다.** dnd-kit의 기본값은 Space와 Enter 둘 다
     * 집기로 쓰는데, 칸 자체가 누르면 열리는 버튼이라 Enter까지 가져가면
     * 키보드로는 픽을 열 수가 없다 — 열리는 대신 집혀 버린다. 실제로 그랬다.
     */
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space'] },
    }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over === null || active.id === over.id) {
      return;
    }
    const from = order.indexOf(String(active.id));
    const to = order.indexOf(String(over.id));
    if (from < 0 || to < 0) {
      return;
    }
    onReorder(arrayMove(order, from, to));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToParentElement]}
      onDragEnd={handleDragEnd}
      accessibility={{ screenReaderInstructions: { draggable: t('picks.reorderHint') } }}
    >
      <SortableContext items={order} strategy={rectSortingStrategy}>
        <ul className={styles.grid}>{children}</ul>
      </SortableContext>
    </DndContext>
  );
}

export function PickGridCell({
  item,
  onClick,
  label,
  sortable = false,
}: {
  item: PickGridItem;
  onClick?: (() => void) | undefined;
  /** 스크린 리더용 설명. 시각적으로는 두 줄 글자가 같은 역할을 한다 */
  label: string;
  /** 끌어서 옮길 수 있는가. 남의 프로필에서는 false다 */
  sortable?: boolean;
}) {
  // 주소가 깨진 사진은 글자 칸으로 되돌린다. 빈 아이콘이 박힌 칸보다 낫다.
  const [broken, setBroken] = useState(false);
  /*
   * 사진이 **도착한 뒤에만** 사진용 차림새로 바꾼다.
   *
   * 미리 바꿔 두면 사진이 오기 전까지 회색 칸 위에 검은 그라데이션만 깔려
   * 깨진 칸처럼 보인다. 느린 연결에서는 그게 첫 화면이 된다.
   */
  const [loaded, setLoaded] = useState(false);
  const hasImage = item.imageUrl !== undefined && item.imageUrl !== '' && !broken;
  const showsPhoto = hasImage && loaded;

  const sort = useSortable({ id: item.categoryId, disabled: !sortable });
  const interactive = onClick !== undefined;
  const Tag = interactive ? 'button' : 'div';

  return (
    <li
      ref={sortable ? sort.setNodeRef : undefined}
      className={styles.cellWrap}
      style={
        sortable
          ? {
              transform: CSS.Transform.toString(sort.transform),
              transition: sort.transition,
              // 집어 든 칸을 위로 올린다. 안 그러면 옆 칸 밑으로 지나간다
              zIndex: sort.isDragging ? 2 : undefined,
            }
          : undefined
      }
      {...(sortable ? sort.attributes : {})}
      {...(sortable ? sort.listeners : {})}
    >
      <Tag
        className={[
          styles.cell,
          showsPhoto ? styles.withImage : styles.noImage,
          sort.isDragging ? styles.dragging : '',
        ]
          .filter(Boolean)
          .join(' ')}
        {...(interactive ? { type: 'button' as const, onClick, 'aria-label': label } : {})}
      >
        {hasImage ? (
          <img
            /*
             * 캐시에 있으면 `onLoad`가 **안 뜬다.** 리액트가 핸들러를 붙이기
             * 전에 이미 다 받아버려서다. 그대로 두면 캐시된 사진이 영영
             * 투명한 채로 남는다 — 두 번째 방문에서 격자가 통째로 비어 보인다.
             * 붙는 순간 `complete`를 확인해 그 경우를 건진다.
             */
            ref={(node) => {
              if (node?.complete === true && node.naturalWidth > 0) {
                setLoaded(true);
              }
            }}
            className={styles.image}
            src={item.imageUrl}
            alt=""
            loading="lazy"
            data-loaded={loaded}
            // 끌고 가는 중에 브라우저 기본 이미지 드래그가 겹치면 안 된다
            draggable={false}
            onLoad={() => setLoaded(true)}
            onError={() => setBroken(true)}
          />
        ) : null}

        <span className={styles.caption} aria-hidden="true">
          <span className={styles.category}>{item.categoryName}</span>
          <span className={styles.name}>{item.itemName}</span>
        </span>
      </Tag>
    </li>
  );
}

/** 격자 끝의 "추가" 칸. 다른 칸과 같은 크기라 줄이 흐트러지지 않는다 */
/**
 * 빈 자리.
 *
 * 아무것도 없는 화면에 추가 칸 하나만 덩그러니 있으면 격자라는 것이 안 보인다.
 * 남은 두 칸을 흐리게 그려 "채워 나가는 자리"라는 모양을 만든다. 누를 수
 * 있는 것이 아니므로 보조기기에는 감춘다.
 */
export function PickGridGhostCell() {
  return (
    <li className={styles.cellWrap} aria-hidden="true">
      <div className={`${styles.cell} ${styles.ghost}`} />
    </li>
  );
}

export function PickGridAddCell({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <li className={styles.cellWrap}>
      <button type="button" className={`${styles.cell} ${styles.add}`} onClick={onClick}>
        <span className={styles.addPlus}>
          <PlusIcon size={18} />
        </span>
        <span className={styles.addLabel}>{label}</span>
      </button>
    </li>
  );
}
